import Box from "@mui/joy/Box";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Typography } from "@mui/joy";

// Форматы даты на обложке.
const MODES = [
  { value: "year", label: "Год" },
  { value: "date", label: "Дата" },
  { value: "range", label: "От и до" },
  { value: "none", label: "Без даты" },
];

// Список годов для выпадашки в календаре (от след. года до 1970).
const CAL_YEARS = (() => {
  const y = new Date().getFullYear();
  const arr = [];
  for (let i = y + 1; i >= 1970; i--) arr.push(i);
  return arr;
})();

const controlStyle = {
  flex: 1, minWidth: 0, border: "none", outline: "none",
  background: "transparent", fontSize: "16px", fontWeight: 500,
  textAlign: "center", color: "inherit", fontFamily: "inherit",
};
const selectStyle = {
  ...controlStyle,
  cursor: "pointer", appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
};

const pad = (n) => String(n).padStart(2, "0");
const todayYear = () => String(new Date().getFullYear());
const todayDate = () => {
  const d = new Date();
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

// Маска DD.MM.YYYY: только цифры, до 8, мягкий кламп дней/месяцев, ленивые точки.
function maskDate(raw) {
  let d = (raw || "").replace(/\D/g, "").slice(0, 8);
  if (!d) return "";
  let day = d.slice(0, 2);
  let month = d.slice(2, 4);
  const year = d.slice(4, 8);
  if (day.length === 2) {
    let n = parseInt(day, 10);
    if (n === 0) n = 1;
    if (n > 31) n = 31;
    day = pad(n);
  }
  if (month.length === 2) {
    let n = parseInt(month, 10);
    if (n === 0) n = 1;
    if (n > 12) n = 12;
    month = pad(n);
  }
  let out = day;
  if (month) out += "." + month;
  if (year) out += "." + year;
  return out;
}

// Красивый компактный date-picker: поле-кнопка + всплывающий календарик.
// value/onChange в формате "DD.MM.YYYY".
function pad2(n) { return String(n).padStart(2, "0"); }
function parseDMY(s) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec((s || "").trim());
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return isNaN(d) ? null : d;
}
function fmtDMY(d) { return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`; }

function MiniCalendar({ value, onPick, onClose }) {
  const sel = parseDMY(value);
  const today = new Date();
  const [view, setView] = useState(() => sel || new Date(today.getFullYear(), today.getMonth(), 1));
  const [menu, setMenu] = useState(null); // "month" | "year" | null
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  // Держим календарь в пределах экрана: если вылезает вправо — прижимаем правым
  // краем (открываем влево); если вылезает вниз — открываем вверх.
  const [posSx, setPosSx] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, M = 8;
    const next = {};
    if (r.right > vw - M) { next.left = "auto"; next.right = 0; }
    if (r.bottom > vh - M) { next.top = "auto"; next.bottom = "calc(100% + 6px)"; }
    setPosSx(next);
  }, []);

  const y = view.getFullYear(), mo = view.getMonth();
  const first = new Date(y, mo, 1);
  const startDow = (first.getDay() + 6) % 7; // Пн=0
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
  const wd = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSel = (d) => sel && sel.getFullYear() === y && sel.getMonth() === mo && sel.getDate() === d;
  const isToday = (d) => today.getFullYear() === y && today.getMonth() === mo && today.getDate() === d;

  return (
    <Box ref={ref} sx={{
      position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 1600,
      bgcolor: "background.surface", border: "1px solid", borderColor: "divider",
      borderRadius: "14px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", p: 1.5,
      width: "min(248px, calc(100vw - 24px))",
      ...(posSx || {}),
    }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, mb: 1, position: "relative" }}>
        <Box onClick={() => { setMenu(null); setView(new Date(y, mo - 1, 1)); }} sx={{ cursor: "pointer", px: 1, py: 0.5, borderRadius: "8px", "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>‹</Box>

        <Box sx={{ display: "flex", gap: 0.5, flex: 1, justifyContent: "center", minWidth: 0 }}>
          <button
            type="button"
            onClick={() => setMenu(menu === "month" ? null : "month")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: "13px", fontWeight: 700, color: "#1a1a1a",
              border: "1px solid rgba(0,0,0,0.15)", borderRadius: "8px",
              padding: "5px 10px", background: "#fff", cursor: "pointer",
              userSelect: "none", WebkitTapHighlightColor: "transparent",
            }}
          >
            {months[mo]} <span style={{ opacity: 0.5, fontSize: "10px" }}>▼</span>
          </button>
          <button
            type="button"
            onClick={() => setMenu(menu === "year" ? null : "year")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: "13px", fontWeight: 700, color: "#1a1a1a",
              border: "1px solid rgba(0,0,0,0.15)", borderRadius: "8px",
              padding: "5px 10px", background: "#fff", cursor: "pointer",
              userSelect: "none", WebkitTapHighlightColor: "transparent",
            }}
          >
            {y} <span style={{ opacity: 0.5, fontSize: "10px" }}>▼</span>
          </button>
        </Box>

        <Box onClick={() => { setMenu(null); setView(new Date(y, mo + 1, 1)); }} sx={{ cursor: "pointer", px: 1, py: 0.5, borderRadius: "8px", "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>›</Box>

        {menu && (
          <div
            style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 5,
              maxHeight: 200, overflowY: "auto", WebkitOverflowScrolling: "touch",
              background: "#fff", border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 4,
            }}
          >
            {(menu === "month"
              ? months.map((m, i) => ({ label: m, val: i }))
              : CAL_YEARS.map((yy) => ({ label: String(yy), val: yy }))
            ).map((opt) => {
              const active = menu === "month" ? opt.val === mo : opt.val === y;
              return (
                <div
                  key={opt.label}
                  onClick={() => {
                    setView(menu === "month" ? new Date(y, opt.val, 1) : new Date(opt.val, mo, 1));
                    setMenu(null);
                  }}
                  style={{
                    padding: "9px 10px", borderRadius: 8, cursor: "pointer",
                    fontSize: "13px", fontWeight: active ? 700 : 500,
                    color: active ? "#834A90" : "#333",
                    background: active ? "rgba(131,74,144,0.08)" : "transparent",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
        {wd.map((w) => (
          <Typography key={w} sx={{ fontSize: "10px", fontWeight: 600, color: "text.tertiary", textAlign: "center", py: 0.5 }}>{w}</Typography>
        ))}
        {cells.map((d, i) => d === null ? <Box key={i} /> : (
          <Box key={i}
            onClick={() => onPick(fmtDMY(new Date(y, mo, d)))}
            sx={{
              height: 28, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "12px", borderRadius: "8px", cursor: "pointer",
              fontWeight: isSel(d) ? 700 : 500,
              color: isSel(d) ? "#fff" : "text.primary",
              bgcolor: isSel(d) ? "primary.500" : "transparent",
              border: (!isSel(d) && isToday(d)) ? "1px solid" : "1px solid transparent",
              borderColor: (!isSel(d) && isToday(d)) ? "primary.300" : "transparent",
              "&:hover": { bgcolor: isSel(d) ? "primary.600" : "rgba(0,0,0,0.06)" },
            }}
          >{d}</Box>
        ))}
      </Box>
    </Box>
  );
}

function DateInput({ value, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          width: "100%", minWidth: 0, height: 34, px: 1,
          display: "flex", alignItems: "center", gap: 0.5,
          cursor: "pointer", overflow: "hidden", whiteSpace: "nowrap",
          fontSize: "13px", color: value ? "text.primary" : "text.tertiary",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: "13px" }}>📅</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value || placeholder}</span>
      </Box>
      {open && (
        <MiniCalendar
          value={value}
          onClose={() => setOpen(false)}
          onPick={(v) => { onChange?.(v); setOpen(false); }}
        />
      )}
    </Box>
  );
}

function DashedField({ children, filled, error = false }) {
  return (
    <Box sx={{
      flex: 1, display: "flex", alignItems: "center",
      border: "1.5px dashed",
      borderColor: error ? "#e04a4a" : (filled ? "divider" : "rgba(0,0,0,0.18)"),
      borderStyle: "dashed",
      borderWidth: "1.5px",
      borderRadius: "10px", px: 1, py: 0.5, minHeight: "34px",
      transition: "border-color 0.2s",
      "&:focus-within": {
        borderColor: "primary.400",
        boxShadow: "0 0 0 2px color-mix(in srgb, var(--joy-palette-primary-400) 15%, transparent)",
      },
    }}>
      {children}
    </Box>
  );
}

// Селект формата — оформлен как явный выпадающий список (заливка + стрелка).
function ModeSelect({ mode, onChange }) {
  return (
    <Box sx={{
      position: "relative", minWidth: 116, display: "flex", alignItems: "center",
      bgcolor: "primary.50",
      border: "1.5px solid",
      borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 45%, transparent)",
      borderRadius: "10px", pl: 1.25, pr: 3.5, py: 0.5, minHeight: "34px",
      cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
      "&:hover": { bgcolor: "primary.100", borderColor: "primary.400" },
      "&:focus-within": {
        borderColor: "primary.400",
        boxShadow: "0 0 0 2px color-mix(in srgb, var(--joy-palette-primary-400) 15%, transparent)",
      },
    }}>
      <select
        value={mode}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ ...selectStyle, textAlign: "left", fontWeight: 600, color: "var(--joy-palette-primary-700)" }}
      >
        {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>
      <KeyboardArrowDownRoundedIcon
        sx={{
          position: "absolute", right: 8, pointerEvents: "none",
          fontSize: 20, color: "primary.500",
        }}
      />
    </Box>
  );
}

export default function CoverDateControls({
  mode = "year", year, dateFrom, dateTo, yearOptions = [],
  onModeChange, onYearChange, onDateFromChange, onDateToChange, error = false,
}) {
  const isRange = mode === "range";

  // Родитель меняет режим и значения одним сохранением.
  const handleModeChange = (next) => {
    if (next !== mode) onModeChange?.(next);
  };

  const singleField = mode === "year" ? (
    <select value={year || todayYear()}
            onChange={(e) => onYearChange?.(e.target.value)} style={selectStyle}>
      {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
    </select>
  ) : (
    <DateInput value={year} placeholder="ДД.ММ.ГГГГ" onChange={onYearChange} />
  );

  if (!isRange) {
    // Режим «Без даты»: показываем только селект формата, поле даты скрыто.
    if (mode === "none") {
      return (
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <ModeSelect mode={mode} onChange={handleModeChange} />
        </Box>
      );
    }
    // Горизонтально: [ формат ▼ | поле ]
    return (
      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <ModeSelect mode={mode} onChange={handleModeChange} />
        <DashedField filled={!!year}>{singleField}</DashedField>
      </Box>
    );
  }

  // "От и до": вертикально — [ формат ▼ ], ниже [ дата от | дата до ]
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      <ModeSelect mode={mode} onChange={handleModeChange} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <DashedField filled={!!dateFrom} error={error && !(dateFrom || "").trim()}>
          <DateInput value={dateFrom} placeholder="ДД.ММ.ГГГГ" onChange={onDateFromChange} />
        </DashedField>
        <DashedField filled={!!dateTo} error={error && !(dateTo || "").trim()}>
          <DateInput value={dateTo} placeholder="ДД.ММ.ГГГГ" onChange={onDateToChange} />
        </DashedField>
      </Box>
    </Box>
  );
}